import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { EnvBanner } from '@grow/ui';
import { AppShell } from './components/AppShell.js';
import { AuthProvider } from './auth/AuthContext.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { RequireAdmin } from './auth/RequireAdmin.js';
import { RequireManager } from './auth/RequireManager.js';

const DashboardScreen = lazy(() =>
  import('./screens/DashboardScreen.js').then((m) => ({ default: m.DashboardScreen })),
);
const ResponsesScreen = lazy(() =>
  import('./screens/ResponsesScreen.js').then((m) => ({ default: m.ResponsesScreen })),
);
const ResponseDetailScreen = lazy(() =>
  import('./screens/ResponseDetailScreen.js').then((m) => ({ default: m.ResponseDetailScreen })),
);
const DetractorsScreen = lazy(() =>
  import('./screens/DetractorsScreen.js').then((m) => ({ default: m.DetractorsScreen })),
);
const ComposeScreen = lazy(() =>
  import('./screens/ComposeScreen.js').then((m) => ({ default: m.ComposeScreen })),
);
const DripScheduleScreen = lazy(() =>
  import('./screens/DripScheduleScreen.js').then((m) => ({ default: m.DripScheduleScreen })),
);
const ContactsScreen = lazy(() =>
  import('./screens/ContactsScreen.js').then((m) => ({ default: m.ContactsScreen })),
);
const ContactDetailScreen = lazy(() =>
  import('./screens/ContactDetailScreen.js').then((m) => ({ default: m.ContactDetailScreen })),
);
const PropertiesScreen = lazy(() =>
  import('./screens/PropertiesScreen.js').then((m) => ({ default: m.PropertiesScreen })),
);
const PropertyDetailScreen = lazy(() =>
  import('./screens/PropertyDetailScreen.js').then((m) => ({ default: m.PropertyDetailScreen })),
);
const AddContactScreen = lazy(() =>
  import('./screens/AddContactScreen.js').then((m) => ({ default: m.AddContactScreen })),
);
const EditContactScreen = lazy(() =>
  import('./screens/EditContactScreen.js').then((m) => ({ default: m.EditContactScreen })),
);
const PortfoliosScreen = lazy(() =>
  import('./screens/PortfoliosScreen.js').then((m) => ({ default: m.PortfoliosScreen })),
);
const SettingsScreen = lazy(() =>
  import('./screens/SettingsScreen.js').then((m) => ({ default: m.SettingsScreen })),
);
const QuestionsScreen = lazy(() =>
  import('./screens/QuestionsScreen.js').then((m) => ({ default: m.QuestionsScreen })),
);
const SurveysScreen = lazy(() =>
  import('./screens/SurveysScreen.js').then((m) => ({ default: m.SurveysScreen })),
);
const PlaygroundScreen = lazy(() =>
  import('./screens/PlaygroundScreen.js').then((m) => ({ default: m.PlaygroundScreen })),
);
const StaffScreen = lazy(() =>
  import('./screens/StaffScreen.js').then((m) => ({ default: m.StaffScreen })),
);
const LoginScreen = lazy(() =>
  import('./screens/LoginScreen.js').then((m) => ({ default: m.LoginScreen })),
);
const SsoCallbackScreen = lazy(() =>
  import('./screens/SsoCallbackScreen.js').then((m) => ({ default: m.SsoCallbackScreen })),
);

// Build-time environment marker. Anything other than 'prod' (staging, local
// dev, an unset var) shows the TEST-ENVIRONMENT ribbon.
const APP_ENV = import.meta.env['VITE_APP_ENV'] ?? 'dev';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div
      style={{
        padding: '36px var(--page-px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 64,
            borderRadius: 8,
            background: '#F0F2EE',
            animation: 'pulse 1.4s ease-in-out infinite',
            opacity: 0.7,
          }}
        />
      ))}
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <EnvBanner show={APP_ENV !== 'prod'} inlineOnPhone />
      <BrowserRouter basename="/admin">
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/auth/callback" element={<SsoCallbackScreen />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route index element={<DashboardScreen />} />
                <Route path="dashboard" element={<DashboardScreen />} />
                <Route path="responses" element={<ResponsesScreen />} />
                <Route path="responses/:id" element={<ResponseDetailScreen />} />
                <Route path="detractors" element={<DetractorsScreen />} />
                <Route path="contacts" element={<ContactsScreen />} />
                <Route path="properties" element={<PropertiesScreen />} />
                <Route path="contacts/new" element={<AddContactScreen />} />
                <Route path="contacts/:id/edit" element={<EditContactScreen />} />
                <Route path="contacts/:id" element={<ContactDetailScreen />} />
                <Route path="properties/:id" element={<PropertyDetailScreen />} />
                <Route path="compose" element={<ComposeScreen />} />
                <Route path="drip" element={<DripScheduleScreen />} />
                {/* Reports merged into the Dashboard (2026-08-04); keep the
                    path alive so existing bookmarks/links still land somewhere. */}
                <Route path="reports" element={<Navigate to="/dashboard" replace />} />
                <Route path="portfolios" element={<PortfoliosScreen />} />
                {/* AI playground: non-prod only (the backend blocks the mint endpoint in prod). */}
                {APP_ENV !== 'prod' && <Route path="playground" element={<PlaygroundScreen />} />}
                {/* Survey editing is Admin/Manager; the API enforces it too. */}
                <Route
                  path="surveys"
                  element={
                    <RequireManager>
                      <SurveysScreen />
                    </RequireManager>
                  }
                />
                <Route
                  path="surveys/:surveyId/questions"
                  element={
                    <RequireManager>
                      <QuestionsScreen />
                    </RequireManager>
                  }
                />
                {/* The question editor used to be General-only at /questions;
                    keep the path pointing somewhere useful for old links. */}
                <Route path="questions" element={<Navigate to="/surveys" replace />} />
                <Route path="settings" element={<SettingsScreen />} />
                {/* Staff/Users management — ADMIN-only, even for Manager (staff-RBAC, 2026-08-10). */}
                <Route
                  path="team"
                  element={
                    <RequireAdmin>
                      <StaffScreen />
                    </RequireAdmin>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
